import React from 'react'
import { useDialog } from '@react-aria/dialog'
import { FocusScope } from '@react-aria/focus'
import { useModal, useOverlay, usePreventScroll } from '@react-aria/overlays'
import { animated, AnimatedComponent } from '@react-spring/web'
import type { CSSProperties, DefaultTheme, StyledComponent } from 'styled-components'
import type { AriaDialogProps } from '@react-types/dialog'
import styled from 'styled-components'
import { cdnRoot, getStaticResourceUrl } from 'utils/cdn'

const DialogContainer = styled.div`
  /* background: #e9f0f4;
  border: 1px solid #cad4da;
  box-sizing: border-box;
  box-shadow: 0px 16px 24px rgba(18, 18, 23, 0.08), 0px 2px 6px rgba(18, 18, 23, 0.02),
    inset 4px 8px 16px rgba(255, 255, 255, 0.32);
  border-radius: 28px;
  outline: none; */

  background: #14280b;
  /* Black */

  border: 8px solid #080a0c;
  /* kekkk */

  box-shadow: -1px -3px 4px rgba(255, 255, 255, 0.2), 0px 16px 32px rgba(27, 26, 37, 0.24),
    0px 12px 24px rgba(27, 26, 37, 0.16), 0px 4px 8px rgba(27, 26, 37, 0.12);
  border-radius: 32px;
  outline: none;
`

const AnimatedDialogContainer = animated(DialogContainer)

interface DialogProps {
  title?: string
}

interface OverlayProps {
  /** Whether the overlay is currently open. */
  isOpen?: boolean
  /** Handler that is called when the overlay should close. */
  onClose?: () => void
  /**
   * Whether to close the overlay when the user interacts outside it.
   * @default false
   */
  isDismissable?: boolean
  /** Whether the overlay should close when focus is lost or moves outside it. */
  shouldCloseOnBlur?: boolean
  /**
   * Whether pressing the escape key to close the overlay should be disabled.
   * @default false
   */
  isKeyboardDismissDisabled?: boolean
  /**
   * When user interacts with the argument element outside of the overlay ref,
   * return true if onClose should be called.  This gives you a chance to filter
   * out interaction with elements that should not dismiss the overlay.
   * By default, onClose will always be called on interaction outside the overlay ref.
   */
  shouldCloseOnInteractOutside?: (element: Element) => boolean
}

type ModalProps = OverlayProps &
  AriaDialogProps &
  DialogProps & {
    containerStyles: CSSProperties
    // Show goo bg behind the modal
    showGooBackground?: boolean
    // Supports react-spring style properties
    gooContainerStyles?: CSSProperties
    // Very specific type -- needs to be a animated(styledcomponent). Only used for overriding the default dialog container
    animatedContainerComponent?: AnimatedComponent<StyledComponent<'div', DefaultTheme, {}, never>>
    allowScroll?: boolean
    hardBlur?: boolean
  }

const ModalDialog: React.FC<ModalProps> = (props) => {
  const { title, children, containerStyles } = props

  // Handle interacting outside the dialog and pressing
  // the Escape key to close the modal.
  const ref = React.useRef<HTMLDivElement | null>(null)
  const { overlayProps, underlayProps } = useOverlay(props, ref)

  // Prevent scrolling while the modal is open, and hide content
  // outside the modal from screen readers.
  usePreventScroll({
    isDisabled: !props.allowScroll,
  })

  const { modalProps } = useModal()

  // Get props for the dialog and its title
  const { dialogProps, titleProps } = useDialog(props, ref)

  const AnimatedContainer = props.animatedContainerComponent ?? AnimatedDialogContainer

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 10000,
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        // paddingTop: 16,
        // paddingBottom: 128,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 'none',
        overflowY: 'auto',
        background: 'rgba(142, 244, 46, 0.4)',
        backdropFilter: props.hardBlur ? 'blur(20px)' : '',
      }}
      {...underlayProps}
    >
      {props.showGooBackground && (
        <animated.div
          style={{
            position: 'absolute',
            ...props.gooContainerStyles,
            zIndex: -1,
          }}
        >
          <img
            alt={'goo background of modal'}
            style={{
              position: 'relative',
              zIndex: -1,
            }}
            src={`${cdnRoot}/images/goop-dialog-behind.png`}
          />
        </animated.div>
      )}

      {/* eslint-disable-next-line jsx-a11y/no-autofocus  */}
      <FocusScope contain restoreFocus autoFocus>
        <AnimatedContainer
          {...overlayProps}
          {...dialogProps}
          {...modalProps}
          ref={ref}
          style={{
            ...containerStyles,
            position: 'relative',
          }}
        >
          {children}
        </AnimatedContainer>
      </FocusScope>
    </div>
  )
}

export { ModalDialog }
